// @ts-nocheck
import { ArrowPathIcon, EyeIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";
import useFetchIDPartner from "./useFetchIDPartner";
import { useTranslation } from "../i18n/LanguageContext";
import FeedbackToast from "../components/FeedbackToast";

function CountdownTimer({ timeUntilNextTrigger, untilText }) {
  const hours = String(
    Math.floor(timeUntilNextTrigger / (1000 * 60 * 60))
  ).padStart(2, "0");
  const minutes = Math.floor(
    (timeUntilNextTrigger % (1000 * 60 * 60)) / (1000 * 60)
  ).toString().padStart(2, "0");
  const seconds = String(
    Math.floor((timeUntilNextTrigger % (1000 * 60)) / 1000)
  ).padStart(2, "0");
  const showText = timeUntilNextTrigger > 0;

  return (
    <div className="text-xs font-thin flex flex-col items-center">
      <div className="flex items-center space-x-1">
        <div className="font-bold">{hours}h</div>
        <span className="text-gray-400">:</span>
        <div className="font-bold">{minutes}m</div>
        <span className="text-gray-400">:</span>
        <div className="font-bold">{seconds}s</div>
      </div>
      {showText && <div className="text-gray-500 text-xs">{untilText}</div>}
    </div>
  );
}
function AccountBalance() {
  const { t } = useTranslation();
  const [showBalance, setShowBalance] = useState(false);
  const [balanceData, setBalanceData] = useState(null);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [checkingCooldown, setCheckingCooldown] = useState(true);
  const [timeUntilNextTrigger, setTimeUntilNextTrigger] = useState(0);
  const [lastRecordTimestamp, setLastRecordTimestamp] = useState(null);
  const [buttonHovered, setButtonHovered] = useState(false);
  const [showCountdown, setShowCountdown] = useState(false);
  const [loadingTrigger, setLoadingTrigger] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const jwtToken = sessionStorage.getItem("jwt");
  const { loading: loadingPartnerID, partnerID } = useFetchIDPartner(jwtToken);

  useEffect(() => {
    if (loadingPartnerID || !partnerID) return;

    const fetchBalanceData = async () => {
      try {
        const URL = require("../service/Service").URL;
        const res = await fetch(`${URL}/api/partners/${partnerID}/balance`, {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
            "Content-Type": "application/json",
          },
        });

        if (!res.ok) {
          throw new Error('Failed to fetch balance data');
        }

        const data = await res.json();
        setBalanceData(data);
      } catch (error) {
        console.error(error);
      }
    };

    fetchBalanceData();
  }, [partnerID, jwtToken, loadingPartnerID]);

  useEffect(() => {
    if (loadingPartnerID || !partnerID) return;

    const fetchTimestamp = async () => {
      try {
        const URL = require("../service/Service").URL;
        const response = await fetch(`${URL}/api/${partnerID}/lastRecordTimestamp`, {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch last record timestamp");
        }

        const timestampData = await response.text();
        setLastRecordTimestamp(timestampData);
      } catch (error) {
        console.error("Error fetching last record timestamp:", error.message);
        setLastRecordTimestamp("");
      } finally {
        setCheckingCooldown(false);
      }
    };

    fetchTimestamp();
  }, [partnerID, jwtToken, loadingPartnerID]);

  useEffect(() => {
    const updateCountdown = () => {
      if (lastRecordTimestamp === null) return;

      if (!lastRecordTimestamp) {
        setButtonDisabled(false);
        setTimeUntilNextTrigger(0);
        setShowCountdown(false);
        return;
      }

      const SIX_HOURS_IN_MILLISECONDS = 6 * 60 * 60 * 1000;
      const currentTime = new Date().getTime();
      const timestamp = new Date(lastRecordTimestamp).getTime();
      if (Number.isNaN(timestamp)) {
        setButtonDisabled(false);
        setTimeUntilNextTrigger(0);
        setShowCountdown(false);
        return;
      }

      const timeDifference = currentTime - timestamp;

      const timeUntilNextTrigger = SIX_HOURS_IN_MILLISECONDS - timeDifference;
      setTimeUntilNextTrigger(
        timeUntilNextTrigger > 0 ? timeUntilNextTrigger : 0
      );

      setButtonDisabled(timeUntilNextTrigger > 0);
      setShowCountdown(timeUntilNextTrigger > 0);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [lastRecordTimestamp]);

  const handleToggle = () => {
    setShowBalance(!showBalance);
  };

  const cooldownTotalMinutes = Math.ceil(timeUntilNextTrigger / (1000 * 60));
  const cooldownHours = Math.floor(cooldownTotalMinutes / 60);
  const cooldownMinutes = cooldownTotalMinutes % 60;

  const readErrorMessage = async (response) => {
    const responseText = await response.text();
    if (!responseText) return t('balance.updateError');

    try {
      return JSON.parse(responseText)?.message || t('balance.updateError');
    } catch {
      return responseText;
    }
  };

  const waitForBalanceUpdate = async () => {
    const URL = require("../service/Service").URL;

    for (let attempt = 0; attempt < 60; attempt += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, 1000));

      const response = await fetch(
        `${URL}/api/syncPartnerTransactions/${partnerID}`,
        { headers: { Authorization: `Bearer ${jwtToken}` } }
      );

      if (!response.ok) {
        throw new Error(await readErrorMessage(response));
      }

      const job = await response.json();
      if (job?.status === "COMPLETED") return job;
      if (job?.status === "ERROR") {
        throw new Error(job?.errorMessage || t('balance.updateError'));
      }
    }

    throw new Error(t('balance.updateError'));
  };

  const refreshBalanceAndCooldown = async () => {
    const URL = require("../service/Service").URL;
    const headers = {
      Authorization: `Bearer ${jwtToken}`,
      Accept: "application/json",
    };

    const [balanceResponse, timestampResponse] = await Promise.all([
      fetch(`${URL}/api/partners/${partnerID}/balance`, { headers }),
      fetch(`${URL}/api/${partnerID}/lastRecordTimestamp`, { headers }),
    ]);

    if (balanceResponse.ok) {
      setBalanceData(await balanceResponse.json());
    }

    if (timestampResponse.ok) {
      setLastRecordTimestamp(await timestampResponse.text());
    }
  };

  const triggerTotalCalculationPartner = async () => {
    try {
      if (!partnerID || !jwtToken) return;

      setLoadingTrigger(true);
      setFeedback(null);

      const URL = require("../service/Service").URL;
      const response = await fetch(`${URL}/api/${partnerID}/triggerTotalCalculation`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${jwtToken}`,
          "Content-Type": "application/json",
        },
      });

      // A conflict means that another request already started this partner's
      // synchronization. In that case, attach to the existing job instead of
      // reporting a false error or starting a duplicate operation.
      if (response.status === 403) {
        throw new Error(t('balance.cooldownBlocked'));
      }

      if (!response.ok && response.status !== 409) {
        throw new Error(await readErrorMessage(response));
      }

      const job = await waitForBalanceUpdate();
      await refreshBalanceAndCooldown();
      const hasTransactions = Array.isArray(job?.data) && job.data.length > 0;
      setFeedback({
        type: hasTransactions ? 'success' : 'info',
        title: t('balance.syncCompletedTitle'),
        message: hasTransactions ? t('balance.updateSuccess') : t('balance.noNewTransactions'),
      });
    } catch (error) {
      console.error("Error updating balance:", error);
      setFeedback({
        type: 'error',
        title: t('balance.syncFailedTitle'),
        message: error?.message || t('balance.updateError'),
      });
    } finally {
      setLoadingTrigger(false);
    }
  };

  return (
    <>
    {feedback && (
      <FeedbackToast
        type={feedback.type}
        title={feedback.title}
        message={feedback.message}
        onClose={() => setFeedback(null)}
      />
    )}
    <div className="mt-8 flex w-full flex-col items-stretch justify-center gap-3 sm:flex-row sm:flex-wrap sm:items-stretch sm:justify-end sm:gap-4">
      <div className="relative inline-flex">
        <button
          type="button"
          className="inline-flex min-h-[46px] w-full items-center justify-between gap-4 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-700 shadow-sm transition hover:border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-cl focus:ring-offset-2 sm:min-w-[240px]"
          onClick={handleToggle}
          aria-pressed={showBalance}
        >
          {showBalance ? (
            <>
              <span className="font-bold">{t('balance.account')}</span>
              <span className="whitespace-nowrap text-xs font-bold text-green-cl">
                {(Math.ceil((balanceData?.balance || 0) * 100) / 100).toFixed(2).replace('.', ',')}&nbsp;€
              </span>
            </>
          ) : (
            <>
              <span className="font-bold">{t('balance.account')}</span>
              <span className="whitespace-nowrap text-xs font-normal text-gray-400">
                {t('balance.show')}
              </span>
            </>
          )}
        </button>
      </div>

      <button
        type="button"
        className="inline-flex min-h-[46px] items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-bold text-gray-700 shadow-sm transition hover:border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-cl focus:ring-offset-2"
        onClick={() => window.location.href = "/user/levantamento"}
      >
        <EyeIcon className="h-4 w-4" />
        {t('balance.viewTransactions')}
      </button>

      <div
        className="relative inline-flex"
        onMouseEnter={() => setButtonHovered(true)}
        onMouseLeave={() => setButtonHovered(false)}
      >
      <button
          onClick={
            buttonDisabled || timeUntilNextTrigger > 0 || loadingTrigger
                ? null
                : triggerTotalCalculationPartner
          }
          className={`relative inline-flex min-h-[46px] items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-bold shadow-sm transition focus:outline-none focus:ring-2 focus:ring-green-cl focus:ring-offset-2 ${
              buttonDisabled || timeUntilNextTrigger > 0 || loadingTrigger
                  ? "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400 shadow-none"
                  : "border-green-cl bg-white text-green-cl hover:bg-green-50"
          }`}
          disabled={checkingCooldown || buttonDisabled || timeUntilNextTrigger > 0 || loadingTrigger}
          title={timeUntilNextTrigger > 0 ? t('balance.cooldown') : t('balance.update')}
      >
        <div className="relative flex items-center gap-2">
          {loadingTrigger ? (
              <>
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                {t('balance.updating')}
              </>
          ) : (
              <>
                <ArrowPathIcon className="h-4 w-4" />
                {timeUntilNextTrigger > 0
                    ? `${t('balance.availableIn')} ${cooldownHours}h ${cooldownMinutes}m`
                    : t('balance.update')}
                {buttonHovered && showCountdown && (
                    <div className="absolute right-0 top-full z-20 mt-2 min-w-[180px] rounded-xl border border-gray-200 bg-white px-4 py-3 text-gray-700 shadow-lg">
                      <p className="mb-1 whitespace-nowrap text-xs font-semibold">{t('balance.nextUpdate')}</p>
                      <CountdownTimer timeUntilNextTrigger={timeUntilNextTrigger} untilText={t('balance.untilNext')} />
                    </div>
                )}
              </>
          )}
        </div>
      </button>
      </div>
    </div>
    </>
  );
}

export default AccountBalance;
